import React from 'react';
import { t } from 'i18next';
import { Button, Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import type { StatusSourceInitialValues } from './types';
import type { StatusSourceValues } from './StatusSourceForm.form';
import { statusSourceForm } from './StatusSourceForm.form';

import type { FormMode } from '@app/Components/Forms/EditableFormShell';
import { EditableFormShell } from '@app/Components/Forms/EditableFormShell';
import type { SelectOption } from '@app/Utilities/SelectMap';
import type { IndUpdateDTO } from '@api/DTO/Ind/Ind/dto';

import { SelectField } from '@app/Components/Forms/Fields/SelectField';
import { TextField } from '@app/Components/Forms/Fields/TextField';
import { SelectWithButtonField } from '@app/Components/Forms/Fields/SelectWithButtonField';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';

/**
 * Props for {@link StatusSourceForm}.
 *
 * Architectural intent:
 * - This component is **form-only** (UI + validation).
 * - It does not own workflows, API calls, or modal logic.
 * - It reacts to external "requests" passed via props.
 */
export type StatusSourceFormProps = {
  /**
   * Controls whether the form is in read or edit mode.
   * The delegator decides when editing is permitted.
   */
  mode?: FormMode;

  /**
   * Partial initial values used to seed the form.
   * These are normalised internally via defaults.
   */
  initialValues?: Partial<StatusSourceInitialValues>;

  /**
   * One-time request to update the `indSource` field.
   *
   * This is typically set after a new user source has been created
   * elsewhere (e.g. via a modal).
   *
   * IMPORTANT:
   * - This is not a controlled value.
   * - The form consumes this request and applies it via `setValue`.
   */
  updatedIndSource: number;

  /** Available options for the status select */
  statusOptions: Array<SelectOption<string>>;

  /** Available options for the source select */
  sourceOptions: Array<SelectOption<number>>;

  /**
   * Indicates whether source options are currently loading.
   * Used to disable editing while dependencies resolve.
   */
  loadingSourceOptions: boolean;

  /** Indicates that the form submission is in progress */
  isSaving?: boolean;

  /**
   * Called when the form is submitted with valid data.
   *
   * The form:
   * - validates with Zod
   * - maps values to an API DTO
   * - delegates all side effects upward
   *
   * @param payload Mapped DTO payload ready for persistence
   */
  onSubmit: (payload: IndUpdateDTO) => Promise<void> | void;

  /**
   * Intent callback fired when the user chooses to add a new user source.
   *
   * The form does not know how this is implemented (modal, page, etc).
   * It simply emits intent upward.
   */
  onAddUserSource: () => void;
};

/**
 * StatusSourceForm
 *
 * UI-only form component responsible for:
 * - rendering inputs
 * - validation
 * - mapping values to DTOs
 * - owning React Hook Form state
 *
 * All workflows (modals, API calls, option refreshing) live in the delegator.
 */
export const StatusSourceForm = ({
  mode = 'read',
  initialValues,
  updatedIndSource,
  statusOptions,
  sourceOptions,
  loadingSourceOptions,
  isSaving = false,
  onAddUserSource,
  onSubmit,
}: StatusSourceFormProps) => {
  /**
   * Compute default values from incoming initial values.
   * Memoised to ensure RHF defaults remain stable.
   */
  const values = React.useMemo(() => statusSourceForm.defaults(initialValues), [initialValues]);

  /**
   * Create and own the React Hook Form instance.
   *
   * Because this component creates the form, it is also responsible
   * for all imperative form updates (setValue, reset, etc).
   */
  const form = useForm<StatusSourceValues>({
    resolver: zodResolver(statusSourceForm.schema),
    defaultValues: values,
    mode: 'onBlur',
  });

  const { setValue } = form;

  /**
   * Internal submit handler.
   *
   * - Parses and validates form values
   * - Maps them to an API DTO
   * - Delegates persistence upward
   *
   * @param values Raw form values
   */
  const handleSubmit = async (values: StatusSourceValues) => {
    const parsed = statusSourceForm.schema.parse(values);
    await onSubmit(statusSourceForm.mapper(parsed));
  };

  /**
   * React to a one-time request to update the `indSource` field.
   *
   * This pattern ensures:
   * - Delegators do not mutate form state directly
   * - Form remains the single owner of its field values
   * - Data flows downward in a predictable way
   */
  React.useEffect(() => {
    if (updatedIndSource != null) {
      setValue('indSource', updatedIndSource, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [updatedIndSource, setValue]);

  /**
   * Editing is disabled while source options are loading
   * to avoid partial or inconsistent state.
   */
  const canEdit = !loadingSourceOptions;

  return (
    <EditableFormShell<StatusSourceValues>
      mode={mode}
      form={form}
      initialValues={values}
      canEdit={canEdit}
      canCancel={true}
      isSaving={isSaving}
      onSubmit={handleSubmit}
    >
      {() => (
        <Row>
          <Col md={3}>
            <SelectField
              name="indStatus"
              label={t('agent.profile.statusSourceForm.statusLabel')}
              options={statusOptions}
            />
          </Col>

          <Col md={3}>
            <SelectWithButtonField
              name="indSource"
              label={t('agent.profile.statusSourceForm.sourceLabel')}
              options={sourceOptions}
              loading={loadingSourceOptions}
              button={
                <Button onClick={onAddUserSource} variant="primary" aria-label={t('common.add')}>
                  <FontAwesomeIcon icon={faPlusCircle} />
                </Button>
              }
            />
          </Col>

          <Col md={3}>
            <TextField
              name="indReferral"
              label={t('agent.profile.statusSourceForm.referredByLabel')}
            />
          </Col>

          <Col md={3}>
            <TextField
              name="indReferralTo"
              label={t('agent.profile.statusSourceForm.referredToLabel')}
            />
          </Col>
        </Row>
      )}
    </EditableFormShell>
  );
};
